(function () {
  // --- rAF polyfill ---
  window.requestAnimationFrame =
    window.requestAnimationFrame ||
    window.mozRequestAnimationFrame ||
    window.webkitRequestAnimationFrame ||
    window.msRequestAnimationFrame ||
    function (fn) {
      return setTimeout(fn, 1000 / 60);
    };

  // --- Wait helper ---
  function waitUntil(conditionFn) {
    return new Promise((resolve) => {
      function check() {
        if (conditionFn()) resolve();
        else requestAnimationFrame(check);
      }
      check();
    });
  }

  // --- CONFIG ---
  const GIFT_VARIANTS = {
    first: "51308199084332", // >= 2000
    second: "51917187907884", // >= 3000
    third: "48162628763948", // >= 5000
  };
  const GIFT_SET = new Set(Object.values(GIFT_VARIANTS));
  const STOPS = [2000, 3000, 5000];
  const LS_KEY = "CP_MS_autoGift";
  const COLLECTION_URL =
    "https://mysanskritam.com/collections/everyday-wear-kurtas/products.json?limit=250";

  // --- STATE ---
  let suppressHooks = false;
  let isReconciling = false;
  let lastFetchUrl = null;
  let validHandles = new Set();
  let collectionLoaded = false;

  // --- FETCH HELPERS ---
  async function fetchJSON(url) {
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`GET ${url} failed ${res.status}`);
    return res.json();
  }

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`POST ${url} failed ${res.status}`);
    return res.json().catch(() => ({}));
  }

  async function fetchCart() {
    return fetchJSON("/cart.js");
  }

  async function loadCollectionHandles() {
    if (collectionLoaded) return;
    try {
      const data = await fetchJSON(COLLECTION_URL);
      if (data?.products?.length) {
        validHandles = new Set(data.products.map((p) => p.handle));
        collectionLoaded = true;
      }
    } catch (err) {
      console.warn("Failed to load collection:", err);
    }
  }

  // --- GIFT LOGIC HELPERS ---
  function calculateNonGiftTotal(cart) {
    let total = cart.total_price;
    for (const item of cart.items) {
      if (GIFT_SET.has(String(item.variant_id))) {
        total -= item.final_price * item.quantity;
      }
    }
    return total / 100;
  }

  function desiredGiftIdFor(total) {
    if (total >= STOPS[2]) return GIFT_VARIANTS.third;
    if (total >= STOPS[1]) return GIFT_VARIANTS.second;
    if (total >= STOPS[0]) return GIFT_VARIANTS.first;
    return null;
  }

  function getGiftQuantities(cart) {
    const q = {};
    for (const id of GIFT_SET) q[id] = 0;
    for (const item of cart.items) {
      const vid = String(item.variant_id);
      if (GIFT_SET.has(vid)) q[vid] = (q[vid] || 0) + item.quantity;
    }
    return q;
  }

  async function addVariantOnce(variantId) {
    suppressHooks = true;
    try {
      await postJSON("/cart/add.js", { id: variantId, quantity: 1 });
    } finally {
      suppressHooks = false;
    }
  }

  async function setVariantQty(variantId, qty) {
    suppressHooks = true;
    try {
      await postJSON("/cart/change.js", { id: variantId, quantity: qty });
    } finally {
      suppressHooks = false;
    }
  }

  async function removeOtherGifts(cart, keepId) {
    const gifts = cart.items.filter((i) => GIFT_SET.has(String(i.variant_id)));
    for (const item of gifts) {
      const vid = String(item.variant_id);
      if (!keepId || vid !== keepId) await setVariantQty(vid, 0);
    }
  }

  async function ensureExactGift(cart, keepId) {
    await removeOtherGifts(cart, keepId);
    cart = await fetchCart();
    const qtyMap = getGiftQuantities(cart);
    const current = qtyMap[keepId] || 0;
    if (current === 1) return;
    if (current <= 0) await addVariantOnce(keepId);
    else if (current > 1) await setVariantQty(keepId, 1);
  }

  async function clearAllGifts(cart) {
    await removeOtherGifts(cart, null);
  }

  // --- DISCOUNT CHECK HELPERS ---
  function hasProductLevelDiscount(cart) {
    if (!cart || !cart.items) return false;

    const matchesOffer = (str) => {
      if (!str || typeof str !== "string") return false;
      const norm = str.toLowerCase().replace(/\s+/g, "");
      return (
        norm.includes("3@2499") ||
        norm.includes("buy3@2499") ||
        (norm.includes("buy3") && norm.includes("2499")) ||
        (norm.includes("3@") && norm.includes("2499"))
      );
    };

    // 1. Check cart-level discount applications
    if (Array.isArray(cart.cart_level_discount_applications)) {
      for (const app of cart.cart_level_discount_applications) {
        if (matchesOffer(app?.title) || matchesOffer(app?.key)) return true;
      }
    }

    // 2. Check line-level discount allocations and discounts on non-gift items
    for (const item of cart.items) {
      if (GIFT_SET.has(String(item.variant_id))) continue;

      if (Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length > 0) {
        for (const alloc of item.line_level_discount_allocations) {
          const title = alloc?.discount_application?.title || alloc?.title;
          if (matchesOffer(title)) return true;
        }
        return true;
      }

      if (Array.isArray(item.discounts) && item.discounts.length > 0) {
        for (const disc of item.discounts) {
          if (matchesOffer(disc?.title)) return true;
        }
        return true;
      }

      if (Array.isArray(item.discount_allocations) && item.discount_allocations.length > 0) {
        for (const alloc of item.discount_allocations) {
          const title = alloc?.discount_application?.title || alloc?.title;
          if (matchesOffer(title)) return true;
        }
        return true;
      }
    }

    return false;
  }

  // --- PROGRESS BAR ---
  function updateProgressBar(cart) {
    const total = calculateNonGiftTotal(cart);
    const barFill = document.querySelector(".CP-pdp_progress-fill");
    const msgSpan = document.querySelector(".CP-pdp_buy-more");
    if (!barFill || !msgSpan) return;

    if (hasProductLevelDiscount(cart)) {
      barFill.className = "CP-pdp_progress-fill threshold-less-1k";
      msgSpan.textContent = "Free gift can't be combined with Bundle Combo offer!";
      return;
    }

    let className = "",
      message = "";
    if (total >= 5000) {
      className = "threshold-5k";
      message = "Free Gift Pack of 4 Mix Print Utility Bag Added worth (₹799)!";
    } else if (total >= 3000) {
      className = "threshold-3k_5k";
      message = `Just ₹${5000 - total} away from Free Pack of 4 Mix Print Utility Bag (₹799)!`;
    } else if (total >= 2000) {
      className = "threshold-2k_3k";
      message = `Just ₹${3000 - total} away from Free Multipurpose UTILITY Flat pouch (₹349)!`;
    } else if (total >= 1000) {
      className = "threshold-1k";
      message = `Just ₹${2000 - total} away from Free Pack of 3 scrunchies (₹199)!`;
    } else {
      className = "threshold-less-1k";
      message = "Please add product in the cart for Free gift!";
    }

    barFill.className = "CP-pdp_progress-fill " + className;
    msgSpan.textContent = message;
  }

  // --- UI REFRESH ---
  async function refreshCartUI() {
    try {
      const cart = await fetchCart();
      if (typeof window.sharedFunctions?.updateSidebarCart === "function") {
        window.sharedFunctions.updateSidebarCart(cart);
      }
      updateProgressBar(cart);
    } catch (err) {
      console.error("Error refreshing cart UI:", err);
    }
  }

  // --- GIFT RECONCILE ---
  async function reconcileGifts() {
    if (isReconciling) return;
    isReconciling = true;
    try {
      let cart = await fetchCart();

      if (hasProductLevelDiscount(cart)) {
        await clearAllGifts(cart);
        localStorage.removeItem(LS_KEY);
        await refreshCartUI();
        return;
      }

      const total = calculateNonGiftTotal(cart);
      const desired = desiredGiftIdFor(total);

      if (!desired) {
        await clearAllGifts(cart);
        localStorage.removeItem(LS_KEY);
      } else {
        await ensureExactGift(cart, desired);
        localStorage.setItem(LS_KEY, desired);
      }

      // Check if gift in updated cart is actually free (final_price === 0)
      const postCart = await fetchCart();
      const nonFreeGift = postCart.items.find(
        (item) => GIFT_SET.has(String(item.variant_id)) && item.final_price > 0
      );
      if (nonFreeGift) {
        await clearAllGifts(postCart);
        localStorage.removeItem(LS_KEY);
      }

      await refreshCartUI();
    } catch (err) {
      console.error("Reconcile error:", err);
    } finally {
      isReconciling = false;
    }
  }

  // --- SHOW/HIDE LOADER ---
  function showLoader() {
    const el = document.querySelector(".CP-loader-overlay");
    if (el) el.classList.add("CP-active");
  }
  function hideLoader() {
    const el = document.querySelector(".CP-loader-overlay");
    if (el) el.classList.remove("CP-active");
  }

  // --- CHECK CONDITION ---
  async function checkCartCondition() {
    await loadCollectionHandles();
    const cart = await fetchCart();

    if (hasProductLevelDiscount(cart)) {
      return { shouldRun: false, cart };
    }

    let count = 0;
    for (const item of cart.items) {
      if (validHandles.has(item.handle)) count += item.quantity;
    }

    const shouldRun = count < 2;
    return { shouldRun, cart };
  }

  // --- MASTER LOGIC ---
  async function runGiftLogic() {
    const { shouldRun, cart } = await checkCartCondition();

    if (shouldRun) {
      document.body.classList.add("CP_MS_PDP_25_V1_ACTIVE");
      await reconcileGifts();
    } else {
      const hasGiftInCart = cart.items.some((item) =>
        GIFT_SET.has(String(item.variant_id))
      );
      if (hasGiftInCart || document.body.classList.contains("CP_MS_PDP_25_V1_ACTIVE")) {
        await clearAllGifts(cart);
        localStorage.removeItem(LS_KEY);
        await refreshCartUI();
        document.body.classList.remove("CP_MS_PDP_25_V1_ACTIVE");
      }
    }
  }

  // --- INITIALIZE ---
  async function init() {
    await runGiftLogic();

    // Loader container setup
    waitUntil(() => document.querySelector(".halo-cart-sidebar")).then(() => {
      const cartSidebar = document.querySelector(".halo-cart-sidebar");
      if (!cartSidebar.querySelector(".CP-loader-overlay")) {
        cartSidebar.insertAdjacentHTML(
          "beforeend",
          `<div class="CP-loader-overlay"><span class="loader"></span></div>`
        );
      }
    });

    // Hook into fetch
    const originalFetch = window.fetch;
    window.fetch = async function (url, options) {
      if (suppressHooks) return originalFetch.apply(this, arguments);
      let isCartChange = false;
      const u = typeof url === "string" ? url : url?.url;
      if (u) {
        isCartChange =
          u.includes("/cart/add.js") ||
          u.includes("/cart/change") ||
          u.includes("/cart/update.js");
      }

      const response = await originalFetch.apply(this, arguments);
      if (isCartChange) {
        showLoader();
        setTimeout(async () => {
          try {
            await runGiftLogic();
          } finally {
            hideLoader();
          }
        }, 100);
      }
      return response;
    };

    // Hook into XHR
    (function () {
      const oldOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        this._url = url;
        return oldOpen.apply(this, arguments);
      };

      const oldSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function (body) {
        if (suppressHooks) return oldSend.apply(this, arguments);
        const u = this._url || "";
        const isCart =
          u.includes("/cart/add.js") ||
          u.includes("/cart/change") ||
          u.includes("/cart/update.js");

        if (isCart) {
          const xhr = this;
          showLoader();
          xhr.addEventListener("load", async () => {
            try {
              await runGiftLogic();
            } finally {
              hideLoader();
            }
          });
        }
        return oldSend.apply(this, arguments);
      };
    })();

    // Re-check on cart updated event
    document.addEventListener("cart:updated", runGiftLogic);
  }

  init();
})();
